import React, { useState, useEffect, useRef } from 'react'

import { CheckboxV2 } from '../../atoms/checkboxV2'

import styles from './checkboxGroup.module.scss'

interface CheckboxOption {
  id: string
  label: string
  checked?: boolean
  disabled?: boolean
}

interface CheckboxGroupProps {
  options: CheckboxOption[]
  onChange?: (selectedOptions: CheckboxOption[]) => void
  className?: string
  checkboxClassName?: string
  maxVisible?: number
  direction?: 'row' | 'column'
  showMoreButtonClassName?: string
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  onChange,
  className = '',
  checkboxClassName = '',
  maxVisible = 8,
  direction = 'column',
  showMoreButtonClassName = '',
}) => {
  const [showAll, setShowAll] = useState(false)
  const [internalOptions, setInternalOptions] = useState<CheckboxOption[]>(options)
  const prevOptionsRef = useRef<CheckboxOption[]>(options)

  useEffect(() => {
    if (options !== prevOptionsRef.current) {
      const mergedOptions = options.map(option => {
        const existingOption = internalOptions.find(internalOption => internalOption.id === option.id)
        return {
          ...option,
          checked: existingOption ? existingOption.checked : option.checked,
        }
      })
      setInternalOptions(mergedOptions)
      prevOptionsRef.current = options
    }
  }, [options, internalOptions])

  const handleCheckboxChange = (id: string) => {
    // Create a new array with the updated option
    const updatedOptions = internalOptions.map(option =>
      option.id === id ? { ...option, checked: !option.checked } : option,
    )

    // Update internal state
    setInternalOptions(updatedOptions)

    // Call onChange with the filtered selected options
    if (onChange) {
      const selectedOptions = updatedOptions.filter(option => option.checked)
      onChange(selectedOptions)
    }
  }

  const visibleOptions = showAll ? internalOptions : internalOptions.slice(0, maxVisible)
  const hiddenCount = internalOptions.length - maxVisible

  return (
    <div className={`${styles.checkboxGroup} ${direction === 'row' ? styles.row : ''} ${className}`}>
      {visibleOptions.map(option => (
        <CheckboxV2
          key={option.id}
          id={option.id}
          label={option.label}
          isChecked={option.checked ?? false}
          disabled={option.disabled}
          onChange={() => {
            handleCheckboxChange(option.id)
          }}
          className={`${styles.checkbox} ${checkboxClassName}`}
        />
      ))}

      {hiddenCount > 0 && (
        <button
          className={`${styles.showMoreButton} ${showMoreButtonClassName}`}
          onClick={() => {
            setShowAll(!showAll)
          }}
        >
          {showAll ? `Show less` : `Show ${hiddenCount} more`}
        </button>
      )}
    </div>
  )
}
